import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { User, Building2, GraduationCap, ArrowRight, Sparkles, Shield, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

export const GatewayPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleIndividualSelect = () => {
    navigate('/auth?type=individual');
  };

  const handleStudentSelect = () => {
    navigate('/student-auth');
  };

  const handleCorporateSelect = () => {
    navigate('/corporate/register');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-[#1a1a2e] to-gray-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50 p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div 
            onClick={() => navigate('/')} 
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-[#CC5500] to-[#C5A065] rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white">LeadEconomics™</span>
          </div>
          <LanguageSelector variant="minimal" />
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 min-h-screen flex items-center justify-center px-4 py-24">
        <div className="max-w-5xl w-full">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              How would you like to access
              <span className="block bg-gradient-to-r from-[#CC5500] to-[#C5A065] bg-clip-text text-transparent">
                LeadEconomics™?
              </span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Choose your access type to begin your leadership assessment journey
            </p>
          </div>

          {/* Access Cards */}
          <div className="space-y-6">
            {/* Corporate / Organisation - Top, Full Width, Large */}
            <Card 
              className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 hover:border-blue-500/50 transition-all duration-300 cursor-pointer group"
              onClick={handleCorporateSelect}
              data-testid="corporate-access-card"
            >
              <CardContent className="p-10">
                <div className="flex flex-col md:flex-row items-start gap-8">
                  <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Building2 className="w-12 h-12 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-3xl font-bold text-white">Corporate / Organisation</h3>
                      <span className="px-4 py-1.5 bg-blue-500/20 rounded-full text-sm text-blue-400 font-medium">ENTERPRISE</span>
                    </div>
                    <p className="text-gray-400 text-lg mb-6 max-w-2xl">
                      Enterprise-grade assessment platform for hiring, team building, and leadership development. 
                      Includes position profiling, candidate assessments, and compatibility analysis.
                    </p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-blue-500" />
                        <span className="text-gray-300">Enterprise Security</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-blue-500" />
                        <span className="text-gray-300">Multi-Currency</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Sparkles className="w-5 h-5 text-blue-500" />
                        <span className="text-gray-300">Credit System</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-blue-500" />
                        <span className="text-gray-300">Compatibility Reports</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-blue-500 text-lg font-medium group-hover:gap-4 transition-all">
                      <span>Register Your Organisation</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Individual & Student - Bottom Row */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Individual / Professional */}
              <Card 
                className="bg-gray-800/50 border-gray-700 hover:border-amber-500/50 transition-all duration-300 cursor-pointer group"
                onClick={handleIndividualSelect}
                data-testid="individual-access-card"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <User className="w-8 h-8 text-amber-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">Individual / Professional</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        Personal leadership assessment for professionals, entrepreneurs, and individuals seeking self-discovery
                      </p>
                      <div className="flex items-center gap-2 text-amber-500 text-sm font-medium group-hover:gap-3 transition-all">
                        <span>Get Started</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-gray-700/50 rounded-full text-xs text-gray-300">Personal Assessment</span>
                      <span className="px-3 py-1 bg-gray-700/50 rounded-full text-xs text-gray-300">Leadership DNA</span>
                      <span className="px-3 py-1 bg-gray-700/50 rounded-full text-xs text-gray-300">Career Guidance</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Student */}
              <Card 
                className="bg-gray-800/50 border-gray-700 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer group"
                onClick={handleStudentSelect}
                data-testid="student-access-card"
              >
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <GraduationCap className="w-8 h-8 text-emerald-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">Student</h3>
                      <p className="text-gray-400 text-sm mb-4">
                        Career guidance and aptitude assessment for students exploring their future paths
                      </p>
                      <div className="flex items-center gap-2 text-emerald-500 text-sm font-medium group-hover:gap-3 transition-all">
                        <span>Begin Assessment</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-gray-700/50 rounded-full text-xs text-gray-300">Career Discovery</span>
                      <span className="px-3 py-1 bg-gray-700/50 rounded-full text-xs text-gray-300">Stream Selection</span>
                      <span className="px-3 py-1 bg-gray-700/50 rounded-full text-xs text-gray-300">University Guidance</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* S3 Global Admin Link */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/s3-admin/login')}
              className="text-gray-500 hover:text-gray-400 text-sm underline-offset-4 hover:underline transition-colors"
              data-testid="s3-admin-link"
            >
              S3 Global Administration
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="absolute bottom-0 left-0 right-0 p-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-600 text-sm">
            © {new Date().getFullYear()} LeadEconomics™ by S3 Global. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default GatewayPage;
